import { useTranslation } from "react-i18next";
import {
  ActorIcon,
  SaveIcon,
  VideoIcon,
} from "@/components/ui/icon";
import { TextButton } from "@/components/common/text-button";
import { cn } from "@/lib/utils";
import { TextInput } from "@/components/common/text-input";
import { EditGrayIcon, IdCardIcon, VoiceIdIcon } from "@/components/agent-dashboard/assets";
import { TruncatedText } from "@/components/common/truncated-text";
import { IconButton } from '@/components/common/icon-button';
import { ChooseVoiceModal } from "@/components/agent-dashboard-user-detail/choose-voice-modal";
import { Spinner } from "@/components/ui/modal";
import { CopyClipboard } from "@/components/common/copy-clipboard";

interface PanelParams {
  actor: Actor;
  voices: Audio[];
  onDownload?: () => void;
  actorName: string;
  actorId: string;
  voiceName: string;
  voiceId: string;
  actorNameError?: string;
  voiceNameError?: string;
  isAllowSave?: boolean;
  isChanged?: boolean;
  duplicateNameMsg?: string;
  saveLoading?: boolean;
  onValueChange?: (key: string, value: string) => void;
  onSave?: () => void;
}

export default function Panel({
  actor,
  voices,
  onDownload,
  actorName,
  actorId,
  voiceName,
  voiceId,
  actorNameError,
  voiceNameError,
  isAllowSave,
  isChanged,
  duplicateNameMsg,
  saveLoading,
  onValueChange,
  onSave,
}: PanelParams) {
  const { t } = useTranslation();

  const handleValueChange = (key: string, value: string) => {
    onValueChange?.(key, value);
  }

  const handleChooseVoice = (voiceId: string) => {
    onValueChange?.('voiceId', voiceId);
  }

  const handleSave = (e) => {
    e.stopPropagation();
    onSave && onSave();
  }

  return (
    <div className="w-88 bg-i-white flex flex-col gap-4 p-6">
      {/* Actor */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <ActorIcon className="w-4 h-4 text-i-sub" />
          <span className="text-sm text-i-sub">{t("modal_video_play.panel.actor")}</span>
        </div>
        <TextInput
          type='text'
          value={actorName}
          className="h-10"
          error={!!actorNameError || !!duplicateNameMsg}
          onChange={(e) => handleValueChange('actorName', e.target.value)}
          helperText={duplicateNameMsg}
        />
      </div>
      {/* Actor ID */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <IdCardIcon className="w-4 h-4 text-i-sub" />
          <span className="text-sm text-i-sub">{t("modal_video_play.panel.actor_id")}</span>
        </div>
        <div className="flex items-center gap-2">
          <TruncatedText
            text={actorId}
            className="max-w-[70%] text-sm text-i-black text-medium"
          />
          <CopyClipboard text={actorId} iconClassName="w-5 h-5" />
        </div>
      </div>

      {/* Voice */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <VideoIcon className="w-4 h-4 text-i-sub" />
          <span className="text-sm text-i-sub">{t("modal_video_play.panel.voice")}</span>
        </div>
        <TextInput
          type='text'
          value={voiceName}
          className="h-10"
          error={!!voiceNameError}
          onChange={(e) => handleValueChange('voiceName', e.target.value)}
        />
      </div>

      {/* Voice ID */}
      <div className="flex flex-col flex-1 space-y-2 overflow-auto">
        <div className="flex items-center gap-2">
          <VoiceIdIcon className="w-4 h-4 text-i-sub" />
          <span className="text-sm text-i-sub">{t("modal_video_play.panel.voice_id")}</span>
        </div>
        <div className="flex items-center space-x-3">
          <TruncatedText
            text={voiceId}
            className="max-w-[70%] text-sm text-i-black text-medium"
          />
          <CopyClipboard text={voiceId} iconClassName="w-5 h-5" />
          {/* 音频超过1个才显示选择语音弹窗 */}
          {voices?.length > 1 && (
            <ChooseVoiceModal
              trigger={
                <IconButton
                  icon={<EditGrayIcon className="w-5 h-5" />}
                  className=" text-i-white rounded-sm"
                />
              }
              voices={voices}
              // loading={loading}
              defaultVoiceId={voiceId}
              onConfirm={handleChooseVoice}
            />
          )}
        </div>
      </div>

      {/* 操作按钮 */}
      <div className="flex gap-3">
        <TextButton
          onClick={handleSave}
          icon={saveLoading ? <Spinner className="w-5 h-5" /> : <SaveIcon className="w-5 h-5" />}
          label={t("modal_video_play.panel.save")}
          disabled={actor.generateStatus !== 2 || saveLoading || !isAllowSave || !isChanged}
          className={cn(
            "flex flex-1 justify-center items-center gap-2 h-10 w-40 bg-i-normal shadow-[0px_1px_2px_rgba(16,24,40,0.05)] rounded-sm transition-colors hover:bg-[rgba(40,47,62,1)] text-sm"
          )}
        />
      </div>
    </div>
  );
}
